import { useEffect, useRef, useCallback } from "react";

import { useSegmentTrack } from "@/hooks/use-segment";
import { perf } from "@/utils/performance-measurements";
import { BuildVersion } from "@/utils/version";

// Module-level state for once-per-session measurement deduplication
// Survives component unmount/remount, resets only on page reload
const globalOnceFlags: Record<string, boolean> = {};

type UsePerformanceOptions = {
    /** Component name for performance tracking */
    component: string;
};

/**
 * React hook for performance measurements using browser Performance API
 *
 * Provides a simple interface for marking performance points and measuring
 * durations in React components.
 */
export const usePerformance = (options: UsePerformanceOptions) => {
    const { component } = options;
    const segmentTrack = useSegmentTrack();
    const unsubscribeRef = useRef<(() => void) | null>(null);

    // Subscribe to measurements
    useEffect(() => {
        // Cleanup previous subscription
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
        }

        unsubscribeRef.current = perf.onComponentMeasurement(component, (measurement) => {
            segmentTrack("performance-measurement", {
                component,
                measurementName: measurement.name,
                duration: measurement.duration,
                startTime: measurement.startTime,
                timestamp: Date.now(),
                url: window.location.pathname,
                dashboardVersion: BuildVersion,
            });
        });

        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
        };
    }, [component, segmentTrack]);

    // Create a performance mark
    const mark = useCallback((name: string) => {
        if (typeof name !== "string" || !name.trim()) {
            console.warn("Invalid mark name:", name);
            return;
        }
        perf.mark(name);
    }, []);

    // Measure time between two marks
    const measure = useCallback((component: string, name: string, startMark: string, endMark?: string) => {
        if (typeof name !== "string" || !name.trim() || typeof startMark !== "string" || !startMark.trim()) {
            console.warn("Invalid measure parameters:", { component, name, startMark, endMark });
            return null;
        }
        return perf.measure(component, name, startMark, endMark);
    }, []);

    // Mark data loading start (only once per session)
    const markDataLoadStart = useCallback(() => {
        const markName = `${component}-load-start`;

        // Use global flag to ensure mark is only created once per session
        const onceKey = `${markName}-once`;
        if (!globalOnceFlags[onceKey]) {
            mark(markName);
            globalOnceFlags[onceKey] = true;
        }

        return markName;
    }, [component, mark]);

    // Mark data loading complete and measure load time (only once per session)
    const markDataLoadComplete = useCallback(
        (startMarkName?: string) => {
            const endMarkName = `${component}-load-complete`;

            // Use global flag to ensure mark and measurement are only created once per session
            const onceKey = `${endMarkName}-once`;
            if (!globalOnceFlags[onceKey]) {
                mark(endMarkName);
                globalOnceFlags[onceKey] = true;

                // Measure if we have a start mark
                if (startMarkName) {
                    const measureName = `${component}-load-time`;

                    return measure(component, measureName, startMarkName, endMarkName);
                }
            }

            return null;
        },
        [component, mark, measure],
    );

    // Mark app ready and measure app load time (only once per session)
    const markAppReady = useCallback(() => {
        const onceKey = "app-ready-once";
        if (!globalOnceFlags[onceKey]) {
            mark("app-react-ready");
            measure("App", "app-load-time", "app-start", "app-react-ready");

            // Log performance summary in development
            if (process.env.NODE_ENV === "development") {
                setTimeout(() => {
                    perf.logMeasurements();
                }, 1000);
            }

            globalOnceFlags[onceKey] = true;
        }
    }, [mark, measure]);

    return {
        mark,
        measure,
        markDataLoadStart,
        markDataLoadComplete,
        markAppReady,
        // Direct access to performance instance
        perf,
    };
};
